import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { isValidPhoneNumber } from 'reservations/base/helpers/phone';
import { RESERVATION, WAITPARTY, WALKIN } from 'reservations/booking/types';
import { useDefaultBookingFormValues } from 'reservations/form/hooks/useFormValues';
import { useBookingForm } from 'reservations/form/providers/BookingFormProvider';

const cardDetailsSchema = z.object({
  address_zip: z.string(),
  brand: z.string(),
  first6: z.string().min(6),
  last4: z.string().min(4),
  exp_month: z.string(),
  exp_year: z.string()
});

const bookingFormSchema = z
  .object({
    date: z.date(),
    partySize: z.number(),
    name: z.string().min(1, { message: 'Name is required' }),
    // TODO: Replace .string() with .email() when upgrading to Zod v4
    email: z
      .string()
      .min(1, { message: 'Email is required' })
      .email({ message: 'Enter a valid email address' }),
    specialOccasion: z.string(),
    note: z
      .string()
      .max(500, { message: 'Notes cannot be longer than 500 characters' }),
    phone: z.string().min(1),
    phoneCode: z.string().min(1),
    fullPhoneNumber: z
      .string()
      .min(1, { message: 'Phone number is required' })
      .refine(isValidPhoneNumber, {
        message: 'Enter a valid phone number'
      }),
    optInMarketingEmail: z.boolean(),
    optInMarketingSms: z.boolean(),
    time: z.number().nullish(),
    availability: z.string().nullish(),
    // TODO: Replace with .literal([RESERVATION, WAITPARTY, WALKIN]) when upgrading to Zod v4
    bookingType: z
      .literal(RESERVATION)
      .or(z.literal(WAITPARTY))
      .or(z.literal(WALKIN))
      .default(RESERVATION),
    savedPaymentMethod: z.string().default(''),
    token: z.string().default(''),
    cardDetails: cardDetailsSchema.nullish().default(null),
    optInChecklessPayments: z.boolean().default(false),
    tipPercentage: z.number().int().nonnegative().default(0)
  })
  .refine(
    ({ bookingType, availability }) =>
      bookingType === RESERVATION ? !!availability : true,
    {
      message: 'Select a valid time for your reservation'
    }
  );

export type BookingFormSchema = z.infer<typeof bookingFormSchema>;

type FormProps = {
  defaultFormValues?: BookingFormSchema;
  children?: ReactNode;
};

function useReservationsForm(defaultFormValues?: BookingFormSchema) {
  const defaultValues = useDefaultBookingFormValues(defaultFormValues);
  return useForm<z.infer<typeof bookingFormSchema>>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues
  });
}

const Form = ({ children, defaultFormValues }: FormProps) => {
  const methods = useReservationsForm(defaultFormValues);
  const { submissionType } = useBookingForm();

  return (
    <FormProvider data-test-id="for\m-provider" {...methods}>
      <form data-testid={`form-${submissionType}`}>{children}</form>
    </FormProvider>
  );
};

export default Form;
